import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase-client";
import { userService } from "@/services/userService";
import { projectService } from "@/services/projectService";
import { invoiceService } from "@/services/invoiceService";
import { shiftsService } from "@/services/shiftService";
import { contactService } from "@/services/contactService";
import { clientService } from "@/services/clientService";
import { timecardService } from "@/services/timecardService";
import { incidentService } from "@/services/incidentService";
import { applicationService } from "@/services/applicationService";
import { useDrawerCache } from "@/providers/DrawerCacheProvider";

interface UseEntityDataProps {
  entityType?: string;
  entityId?: string;
  enabled?: boolean;
}

export function useEntityData({ entityType, entityId, enabled = true }: UseEntityDataProps) {
  const [entityData, setEntityData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cache = useDrawerCache();

  // Fetch entity data from API
  const fetchFromApi = useCallback(async (): Promise<any> => {
    if (!entityType || !entityId) return null;

    // For users, use the service method to get complete data including link tables
    if (entityType === "user") {
      return await userService.getUserById(entityId);
    }

    // For projects, use the service method to get complete data including link tables
    if (entityType === "project") {
      return await projectService.getProjectById(entityId);
    }

    // For shifts, use the service method to get complete data including link tables
    if (entityType === "shift") {
      return await shiftsService.getShiftById(entityId);
    }

    // For contacts, use the service method to get complete data including link tables
    if (entityType === "contact") {
      return await contactService.getContactById(entityId);
    }

    // For clients, use the service method to get complete data including link tables
    if (entityType === "client") {
      return await clientService.getClientById(entityId);
    }

    if (entityType === "invoice") {
      return await invoiceService.getInvoiceById(entityId);
    }

    if (entityType === "time_card") {
      return await timecardService.getTimecardById(entityId);
    }

    if (entityType === "incident") {
      return await incidentService.getIncidentById(entityId);
    }

    if (entityType === "application") {
      return await applicationService.getApplicationById(entityId);
    }

    if (entityType === "billing_item") {
      // Fetch billing item with project and client relations
      const { data, error: fetchError } = await supabase
        .from("billing_items")
        .select("*, client: clients(id, name), project: projects(id, name)")
        .eq("id", entityId)
        .single();
      if (fetchError) throw fetchError;
      return data;
    }

    // Fallback to direct supabase query for other entity types
    let tableName = "";
    switch (entityType) {
      case "project":
        tableName = "projects";
        break;
      case "client":
        tableName = "clients";
        break;
      case "user":
        tableName = "users";
        break;
      case "time_card":
        tableName = "time_cards";
        break;
      case "invoice":
        tableName = "invoices";
        break;
      case "estimate":
        tableName = "estimates";
        break;
      case "expense":
        tableName = "expenses";
        break;
      case "contact":
        tableName = "contacts";
        break;
      case "shift":
        tableName = "shifts";
        break;
      case "course":
        tableName = "courses";
        break;
      case "incident":
        tableName = "incidents";
        break;
      case "investigation":
        tableName = "investigations";
        break;
      case "billing_item":
        tableName = "billing_items";
        break;
      case "payroll":
        tableName = "payrolls";
        break;
      default:
        throw new Error(`Unknown entity type: ${entityType}`);
    }

    // Build select query - include relations for specific entity types
    let selectQuery = "*";
    if (entityType === "shift") {
      selectQuery = `*,
        project: projects (id, name, client_id, client: clients(id, name)),
        shift_positions(
          *,
          shift_users(
            *,
            user:users(id, first_name, last_name, email_address, preferred_name)
          )
        )`;
    } else if (entityType === "project") {
      selectQuery = "*, client: clients(*), shifts(start_time, end_time)";
    } else if (entityType === "time_card") {
      // selectQuery = `id, status, shift:shifts(id, name), project_id, project:shifts(projects(id, name)), client:shifts(projects(clients(id, name))), user_id, meta_data`;
    } else if (entityType === "invoice") {
      selectQuery = "*, project: projects(id, name, client_id, client: clients(id, name))";
    } else if (entityType === "estimate") {
      selectQuery =
        "*, project: projects(id, name, client_id, client: clients(id, name)), status: meta_data->>status, estimate_number: meta_data->>estimate_number, estimate_date: meta_data->>estimate_date, expiry_date: meta_data->>expiry_date, amount_due: meta_data->>amount_due, line_items: meta_data->line_items";
    }

    const { data, error: fetchError } = await supabase.from(tableName).select(selectQuery).eq("id", entityId).single();

    if (fetchError) throw fetchError;
    return data;
  }, [entityType, entityId]);

  // Load entity data with caching
  const loadEntityData = useCallback(async () => {
    if (!entityType || !entityId) {
      setEntityData(null);
      setLoading(false);
      return;
    }

    try {
      // Check cache first
      const cached = cache.get(entityType, entityId);
      if (cached) {
        setEntityData(cached);
        // If not stale, we're done - no need to fetch
        if (!cache.isStale(entityType, entityId)) {
          setLoading(false);
          return;
        }
        // If stale, show cached data but fetch in background (don't show loading)
      } else {
        // No cache, show loading spinner
        setLoading(true);
      }

      setError(null);

      // Fetch fresh data from API
      const data = await fetchFromApi();
      setEntityData(data);

      // Store in cache
      if (data) {
        cache.set(entityType, entityId, data);
      }
    } catch (err: any) {
      setError(err.message);
      setEntityData(null);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, cache, fetchFromApi]);

  // Refetch with cache invalidation
  const refetch = useCallback(async () => {
    if (entityType && entityId) {
      cache.invalidate(entityType, entityId);
    }
    await loadEntityData();
  }, [entityType, entityId, cache, loadEntityData]);

  // Load data when params change
  useEffect(() => {
    if (enabled) {
      loadEntityData();
    } else {
      setEntityData(null);
      setLoading(false);
    }
  }, [entityType, entityId, enabled, loadEntityData]);

  return {
    entityData,
    loading,
    error,
    refetch,
  };
}
